import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';

const Reports: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  // Check if user has reports.view permission or is Super Admin
  const hasViewReportsAccess =
    hasPermission('reports.view') ||
    (user && (user.role_id === 1 || user.role_name === 'Super Admin'));

  // Check if user has reports.sales_reports permission
  const hasSalesReportsAccess = hasPermission('reports.sales_reports');
  const hasExecutiveDashboard = hasPermission('reports.executive_dashboard');
  const is_sales_agent =
    user?.role_name === 'Sales Agent' ||
    (user?.sales_agent_id != null && String(user.sales_agent_id).trim() !== '');

  // Check if user is Production Admin (role id 7)
  const isProductionAdmin = user && (user.role_id === 7 || user.role_name === 'Production Admin');

  const executiveDashboardCard = {
    id: 'executive-dashboard',
    name: '📈 Executive Dashboard',
    path: '/reports/executive-dashboard',
    description: is_sales_agent
      ? 'Your own sales: KPIs, customers, products, territories, year-end, and breakdown'
      : 'High-level KPIs, year-end projections, and top decliners across customers, territories, and products',
  };

  const reportTabs = is_sales_agent
    ? hasExecutiveDashboard
      ? [executiveDashboardCard]
      : []
    : [
    ...(hasViewReportsAccess
      ? [
          {
            id: 'view',
            name: '📊 View Reports',
            path: '/reports/view',
            description: 'Generate and view various reports for your production data',
          },
          {
            id: 'production-forecast',
            name: '🤖 Production forecast seasons',
            path: '/reports/production-forecast',
            description:
              'Weekly sales vs production with Hebrew-calendar holiday seasons for Auto Create PO products',
          },
        ]
      : []),
    ...(hasSalesReportsAccess && !isProductionAdmin
      ? [
          {
            id: 'sales',
            name: '💰 Sales Reports',
            path: '/reports/sales',
            description: 'Year-over-year customer sales analysis with monthly breakdowns',
          },
        ]
      : []),
    ...(hasExecutiveDashboard && !isProductionAdmin ? [executiveDashboardCard] : []),
  ];

  const isActiveTab = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">📊 Reports</h1>
        <p className="page-subtitle">
          Generate reports, configure alerts, and track production data
        </p>
      </div>

      <div className="reports-navigation">
        <div className="reports-grid">
          {reportTabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.path}
              className={`report-card ${isActiveTab(tab.path) ? 'active' : ''}`}
            >
              <div className="report-card-header">
                <h3>{tab.name}</h3>
              </div>
              <div className="report-card-content">
                <p>{tab.description}</p>
              </div>
              <div className="report-card-arrow">
                <span>→</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        .reports-navigation {
          margin-top: 2rem;
        }

        .reports-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .report-card {
          display: block;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 1.5rem;
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .report-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
          transform: translateY(-2px);
        }

        .report-card.active {
          border-color: #3b82f6;
          background: #f8fafc;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        }

        .report-card-header h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }

        .report-card-content p {
          margin: 0;
          color: #6b7280;
          line-height: 1.5;
        }

        .report-card-arrow {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          font-size: 1.25rem;
          color: #9ca3af;
          transition: all 0.2s ease;
        }

        .report-card:hover .report-card-arrow {
          color: #3b82f6;
          transform: translateX(4px);
        }

        .report-card.active .report-card-arrow {
          color: #3b82f6;
        }
      `}</style>
    </div>
  );
};

export default Reports;
